import type {
  LeaderboardHoleScoreFragment,
  PlayerSummaryFragment,
} from '../../__generated__/hooks'
import DetailSheet from './DetailSheet'
import MiniScorecard, { type CourseHoleSummary } from './MiniScorecard'

/**
 * Team detail sheet — Scramble and BestBall use this for team rows.
 * Hero shows team name with members listed in subtitle, primary stat
 * = score, secondary = points + strokes. The scorecard renders the
 * team ball (one score per hole, not per-member).
 *
 * Shape decoupled from the underlying fragment because Scramble +
 * BestBall team entries differ slightly and the caller knows how to
 * pluck the right fields out.
 */
const TeamDetail = ({
  open,
  onClose,
  data,
  courseHoles,
}: {
  open: boolean
  onClose: () => void
  data: {
    rank?: number | null
    teamName: string
    flightNumber: number | null | undefined
    members: readonly PlayerSummaryFragment[]
    score: number
    strokes: number
    strokesNet: number
    points: number
    holesRegistred: number
    scores: readonly LeaderboardHoleScoreFragment[]
  } | null
  courseHoles: readonly CourseHoleSummary[]
}) => {
  if (!data) return null
  let formattedScore: string
  if (data.score === 0) formattedScore = 'E'
  else if (data.score > 0) formattedScore = `+${data.score}`
  else formattedScore = String(data.score)
  const strokes = data.strokesNet || data.strokes
  const memberList = data.members.map((m) => m.name).join(', ')
  const subtitleParts: string[] = [`${data.holesRegistred} spilte hull`]
  if (data.flightNumber != null) {
    subtitleParts.push(`flight #${data.flightNumber}`)
  }
  if (memberList) subtitleParts.push(memberList)
  return (
    <DetailSheet
      open={open}
      onClose={onClose}
      rank={data.rank ?? null}
      title={data.teamName}
      subtitle={subtitleParts.join(' · ')}
      primary={formattedScore}
      secondary={`${data.points} pts · ${strokes} slag`}
    >
      <section className="rounded-xl bg-paper shadow-sm overflow-hidden">
        <div className="px-3 pt-3 pb-1 up text-ink-500 text-[10px]">
          Scorekort
        </div>
        <MiniScorecard courseHoles={courseHoles} scores={data.scores} />
      </section>
    </DetailSheet>
  )
}

export default TeamDetail
